/**
 * Payment Hook
 * 
 * Hook to access payment context
 * 
 * @module services/internalApi/payment/context
 */

import React from 'react';
import { PaymentContext, type PaymentContextValue } from './PaymentContext';

/**
 * Hook to access payment context
 * 
 * @throws Error if used outside PaymentProvider
 * 
 * @example
 * ```typescript
 * const { state, setEmail, setIsProcessing } = usePayment();
 *
 * // Set user email
 * setEmail('user@example.com');
 *
 * // Read the shared email / processing state
 * const { email, isProcessing } = state;
 * ```
 */
export function usePayment(): PaymentContextValue {
  const context = React.useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within PaymentProvider');
  }
  return context;
}

// Also export as usePaymentContext for backwards compatibility
export { usePayment as usePaymentContext };
